import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ApiExError } from '@shared';

interface ConcurrentSearchState {
  searches: Record<string, {
    data: OpenAiCompletionDbRecord | null;
    error: ApiExError | null;
    isLoading: boolean;
  }>;
}

const initialState: ConcurrentSearchState = {
  searches: {},
};

const concurrentSearchSlice = createSlice({
  name: 'concurrentSearch',
  initialState,
  reducers: {
    // API triggers (for saga/middleware)
    postConcurrentSearchRequest: (
      _state,
      _action: PayloadAction<ConcurrentSearchRequest & { searchKey: string }>,
    ) => {},
    postConcurrentSearchResponse(
      state,
      action: PayloadAction<OpenAiCompletionDbRecord & { searchKey: string }>,
    ) {
      const { searchKey, ...data } = action.payload;
      if (!state.searches[searchKey]) {
        state.searches[searchKey] = { data: null, error: null, isLoading: false };
      }
      state.searches[searchKey].data = data;
      state.searches[searchKey].isLoading = false;
      state.searches[searchKey].error = null;
    },
    postConcurrentSearchFailure: (
      state, 
      action: PayloadAction<ApiExError>
    ) => {
      const error = action.payload;
      const searchKey = error.metadata?.searchKey as string;
      
      if (!searchKey) {
        return;
      }
      
      if (!state.searches[searchKey]) {
        state.searches[searchKey] = { data: null, error: null, isLoading: false };
      }
      state.searches[searchKey].data = null;
      state.searches[searchKey].isLoading = false;
      state.searches[searchKey].error = error;
    },

    // Local state management
    setConcurrentSearchLoading: (state, action: PayloadAction<{ searchKey: string; isLoading: boolean }>) => {
      const { searchKey, isLoading } = action.payload;
      if (!state.searches[searchKey]) {
        state.searches[searchKey] = { data: null, error: null, isLoading: false };
      }
      state.searches[searchKey].isLoading = isLoading;
    },
    resetConcurrentSearch: (state, action: PayloadAction<string>) => {
      const searchKey = action.payload;
      if (state.searches[searchKey]) {
        state.searches[searchKey] = { data: null, error: null, isLoading: false };
      }
    },
    clearAllConcurrentSearches: (state) => {
      state.searches = {};
    },
  },
});

export const {
  postConcurrentSearchRequest,
  postConcurrentSearchResponse,
  postConcurrentSearchFailure,
  setConcurrentSearchLoading,
  resetConcurrentSearch,
  clearAllConcurrentSearches,
} = concurrentSearchSlice.actions;

export const concurrentSearchSliceReducer = concurrentSearchSlice.reducer;